import axios from 'axios';
import { apiUrl, getAuthHeaders } from './network_calls';

export type AffiliateRewardKind =
  | 'verified_signup'
  | 'first_token_purchase'
  | 'first_paid_subscription';

export type AffiliateSummary = {
  enabled: boolean;
  link: string;
  referralCode: string;
  balances: {
    availableTokens: number;
    pendingTokens: number;
    inReviewTokens: number;
    claimedTokens: number;
    reversedTokens: number;
    rewardDebtTokens: number;
  };
  counts: { referredUsers: number; rewards: number };
  recentActivity: Array<{
    rewardId: string;
    kind: AffiliateRewardKind;
    tokens: number;
    status: string;
    earnedAt: string;
    availableAt: string;
  }>;
  rules: {
    verifiedSignupTokens: number;
    firstTokenPurchaseTokens: number;
    firstPaidSubscriptionTokens: number;
    incubationDays: number;
    attributionWindowDays: number;
    learnMoreUrl: string;
  };
};

export type AffiliateProgramRules = AffiliateSummary['rules'] & {
  enabled: boolean;
};

export const getAffiliateProgramRules = async (): Promise<AffiliateProgramRules> => {
  const response = await axios.get(apiUrl('/affiliate/program'));
  return response.data as AffiliateProgramRules;
};

export const getAffiliateDeviceId = (): string => {
  const key = 'fable-affiliate-device-id-v1';
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(key, created);
  return created;
};

export const getAffiliateLink = async () => {
  const response = await axios.post(
    apiUrl('/affiliate/link'),
    { deviceId: getAffiliateDeviceId() },
    { headers: await getAuthHeaders() },
  );
  return response.data as { referralCode: string; link: string };
};

export const bindAffiliateReferral = async (referralCode: string) => {
  const response = await axios.post(
    apiUrl('/affiliate/bind'),
    { referralCode, deviceId: getAffiliateDeviceId(), platform: 'web' },
    { headers: await getAuthHeaders() },
  );
  return response.data as { attributed: boolean; reason?: string };
};

export const getAffiliateRewards = async (): Promise<AffiliateSummary> => {
  const response = await axios.get(
    apiUrl(`/affiliate/rewards?deviceId=${encodeURIComponent(getAffiliateDeviceId())}`),
    { headers: await getAuthHeaders() },
  );
  return response.data as AffiliateSummary;
};

export const claimAffiliateRewards = async () => {
  const response = await axios.post(
    apiUrl('/affiliate/claim'),
    {},
    { headers: await getAuthHeaders() },
  );
  return response.data as {
    claimedTokens: number;
    claimId?: string;
    remainingAvailableTokens: number;
    heldForReviewTokens?: number;
  };
};
